<template>
  <div>
    <vx-card>
      <div class="vx-row mb-4">
        <div class="vx-col w-full flex justify-between items-center">
          <h4>Envio em massa i4Pro</h4>
          <div class="flex">
            <vs-button color="success" class="mr-2" @click="openCreateModal">
              Criar novo envio em massa
            </vs-button>
            <vs-button color="primary" type="border" @click="loadBatches">
              Atualizar
            </vs-button>
          </div>
        </div>
      </div>

      <vs-table
        :data="batches"
        noDataText="Nenhum envio em massa encontrado"
        class="tablePrincipal"
      >
        <template slot="thead">
          <vs-th>ID</vs-th>
          <vs-th>Data de início</vs-th>
          <vs-th>Data de fim</vs-th>
          <vs-th>Tipo de apólice</vs-th>
          <vs-th>Total de apólices</vs-th>
        </template>
        <template slot-scope="{ data }">
          <tbody>
            <vs-tr v-for="item in data" :key="item.id" :data="item">
              <vs-td>{{ item.id }}</vs-td>
              <vs-td>{{ formatDate(item.startDate) }}</vs-td>
              <vs-td>{{ formatDate(item.endDate) }}</vs-td>
              <vs-td>{{ getPolicyTypeLabel(item.policyType) }}</vs-td>
              <vs-td>
                {{
                  item.totalPolicies !== null && item.totalPolicies !== undefined
                    ? item.totalPolicies
                    : '-'
                }}
              </vs-td>
            </vs-tr>
          </tbody>
        </template>
      </vs-table>
    </vx-card>

    <Modal
      classContent="i4pro-batch-modal"
      title="Novo envio em massa i4Pro"
      :active.sync="createModalActive"
      @close="closeCreateModal"
    >
      <div class="vx-row">
        <div class="vx-col w-full">
          <label class="label-text">Tipo de apólice</label>
          <vs-select v-model="form.policyType" class="w-full">
            <vs-select-item value="" text="Selecione" />
            <vs-select-item
              v-for="option in policyTypeOptions"
              :key="`policy-type-${option.value}`"
              :value="option.value"
              :text="option.label"
            />
          </vs-select>
        </div>
        <div class="vx-col w-full mt-4">
          <label class="label-text">Data de início</label>
          <vs-input v-model="form.startDate" type="date" class="w-full" />
        </div>
        <div class="vx-col w-full mt-4">
          <label class="label-text">Data de fim</label>
          <vs-input
            v-model="form.endDate"
            :min="form.startDate || null"
            type="date"
            class="w-full"
          />
        </div>
        <div class="vx-col w-full mt-2" v-if="isDateRangeInvalid">
          <span class="text-danger text-sm">
            A data de início deve ser menor ou igual à data de fim.
          </span>
        </div>
      </div>
      <div class="vx-row mt-4">
        <div class="vx-col w-full flex justify-end">
          <vs-button
            type="border"
            color="dark"
            class="mr-2"
            @click="closeCreateModal"
          >
            Cancelar
          </vs-button>
          <vs-button
            color="primary"
            :disabled="isCreateDisabled"
            @click="createBatch"
          >
            Criar envio
          </vs-button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import moment from 'moment';
import Modal from '../../../components/Modal.vue';
import i4proApi from '../../../services/i4pro.api';
import { TIPOS_APOLICES } from '../../../utils/constants';
import notify from '../../../utils/notify';

export default {
  name: 'I4ProBatches',
  components: {
    Modal,
  },
  data() {
    return {
      batches: [],
      createModalActive: false,
      creatingBatch: false,
      form: {
        policyType: '',
        startDate: '',
        endDate: '',
      },
    };
  },
  computed: {
    policyTypeOptions() {
      return Object.entries(TIPOS_APOLICES).map(([label, value]) => ({
        label,
        value,
      }));
    },
    isDateRangeInvalid() {
      if (!this.form.startDate || !this.form.endDate) {
        return false;
      }
      return moment(this.form.startDate).isAfter(moment(this.form.endDate));
    },
    isCreateDisabled() {
      return (
        this.creatingBatch ||
        !this.form.policyType ||
        !this.form.startDate ||
        !this.form.endDate ||
        this.isDateRangeInvalid
      );
    },
  },
  mounted() {
    this.loadBatches();
  },
  methods: {
    extractBatches(payload) {
      if (Array.isArray(payload)) {
        return payload;
      }
      if (payload && Array.isArray(payload.data)) {
        return payload.data;
      }
      if (payload && Array.isArray(payload.items)) {
        return payload.items;
      }
      return [];
    },
    normalizeBatch(item) {
      return {
        id: item.id,
        startDate: item.startDate,
        endDate: item.endDate,
        policyType: item.policyType,
        totalPolicies: item.totalPolicies,
      };
    },
    formatDate(date) {
      if (!date) {
        return '-';
      }
      const parsedDate = moment(date);
      if (!parsedDate.isValid()) {
        return date;
      }
      return parsedDate.format('DD/MM/YYYY');
    },
    getPolicyTypeLabel(policyType) {
      const option = this.policyTypeOptions.find(
        (item) => String(item.value) === String(policyType),
      );
      if (option) {
        return option.label;
      }
      return policyType || '-';
    },
    resetForm() {
      this.form = {
        policyType: '',
        startDate: '',
        endDate: '',
      };
    },
    openCreateModal() {
      this.createModalActive = true;
    },
    closeCreateModal() {
      this.createModalActive = false;
      this.resetForm();
    },
    async loadBatches(silent = false) {
      if (!silent) {
        this.$vs.loading();
      }
      try {
        const response = await i4proApi.getBatches();
        this.batches = this.extractBatches(response).map((item) =>
          this.normalizeBatch(item),
        );
      } catch (error) {
        notify.notifyFail(this.$vs, 'Erro ao buscar envios em massa do i4Pro.');
      } finally {
        if (!silent) {
          this.$vs.loading.close();
        }
      }
    },
    async createBatch() {
      if (this.isCreateDisabled) {
        return;
      }
      this.creatingBatch = true;
      this.$vs.loading();
      try {
        await i4proApi.createBatch({
          policyType: this.form.policyType,
          startDate: this.form.startDate,
          endDate: this.form.endDate,
        });
        notify.notifySuccess(this.$vs, 'Envio em massa criado com sucesso.');
        this.closeCreateModal();
        await this.loadBatches(true);
      } catch (error) {
        notify.notifyFail(this.$vs, 'Erro ao criar envio em massa do i4Pro.');
      } finally {
        this.creatingBatch = false;
        this.$vs.loading.close();
      }
    },
  },
};
</script>

<style scoped>
.label-text {
  font-size: 12px;
  font-weight: 300;
  color: #333;
}

.i4pro-batch-modal {
  overflow: visible;
}
</style>
